import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spinner,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import useApi from "../../../shared/hooks/useApi";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import { PatientId } from "../../../shared/schema/schema";
import fetchMedicationPredictionsFromClinicalTables from "../api/clinical-tables.api";
import { MEDICATION_FREQUENCY_OPTIONS } from "../constants/medication.constants";
import { Medication, MedicationPrediction } from "../types/medication.types";

interface Props {
  isOpen: boolean;
  patientId: PatientId;
  medication?: Medication | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AddEditMedicationModal({
  isOpen,
  patientId,
  medication,
  onClose,
  onSuccess,
}: Props) {
  const { api, queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const isEditing = medication !== null && medication !== undefined;

  const [medicationName, setMedicationName] = useState(() => medication?.name ?? "");
  const [dosage, setDosage] = useState(() => medication?.dosage ?? "");
  const [frequency, setFrequency] = useState(() => medication?.frequency || "");
  const [administrationNotes, setAdministrationNotes] = useState(
    () => medication?.administrationNotes ?? ""
  );
  const [isOtherFrequency, setIsOtherFrequency] = useState(() => {
    return medication?.frequency
      ? !MEDICATION_FREQUENCY_OPTIONS.includes(medication.frequency as any)
      : false;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPrediction, setSelectedPrediction] = useState<MedicationPrediction | null>(() =>
    medication
      ? {
          id: medication.id,
          name: medication.name,
          doseOptions: medication.doseOptions,
        }
      : null
  );
  const [isOtherDose, setIsOtherDose] = useState(() => {
    if (medication?.doseOptions && medication.doseOptions.length > 0) {
      return !medication.doseOptions.includes(medication.dosage);
    }
    return false;
  });
  const { isOpen: isPopoverOpen, onOpen: onPopoverOpen, onClose: onPopoverClose } = useDisclosure();
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 300);

  const { data: predictions = [], isLoading: isSearching } = useQuery({
    ...queries.patient.searchMedications({
      searchString: debouncedSearchTerm,
      offset: 0,
      limit: 20,
    }),

    queryKey: ["medications", "search", debouncedSearchTerm],
    queryFn: async () => {
      try {
        return await fetchMedicationPredictionsFromClinicalTables({
          term: debouncedSearchTerm,
          limit: 20,
          offset: 0,
        });
      } catch (error) {
        return await api.get("./medications/search", {
          query: { searchString: debouncedSearchTerm, offset: 0, limit: 20 },
        });
      }
    },
    enabled: debouncedSearchTerm.length > 0 && isPopoverOpen,
  });

  const createMutation = useMutation({
    ...mutations.patient.createMedication(patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.patient.medications(patientId).queryKey });
      onSuccess?.();
      handleClose();
    },
  });

  const updateMutation = useMutation({
    ...mutations.patient.updateMedication(patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.patient.medications(patientId).queryKey });
      onSuccess?.();
      handleClose();
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const resetForm = () => {
    setMedicationName("");
    setDosage("");
    setFrequency("");
    setAdministrationNotes("");
    setSearchTerm("");
    setSelectedPrediction(null);
    setIsOtherDose(false);
    setIsOtherFrequency(false);
  };

  const handleClose = () => {
    if (!isEditing) {
      resetForm();
    }
    onClose();
    onPopoverClose();
  };

  const handleMedicationNameChange = (value: string) => {
    setMedicationName(value);
    setSearchTerm(value);
    if (value.length > 0) {
      onPopoverOpen();
    } else {
      onPopoverClose();
      setSelectedPrediction(null);
    }
  };

  const handlePredictionSelect = (prediction: MedicationPrediction) => {
    setMedicationName(prediction.name);
    setSelectedPrediction(prediction);
    setIsOtherDose((prediction.doses ?? []).length === 0);
    setSearchTerm("");
    onPopoverClose();
    setDosage("");
  };

  const handleDoseChange = (value: string) => {
    if (value === "other") {
      setIsOtherDose(true);
      setDosage("");
    } else {
      setIsOtherDose(false);
      setDosage(value);
    }
  };

  const handleFrequencyChange = (value: string) => {
    if (value === "other") {
      setIsOtherFrequency(true);
      setFrequency("");
    } else {
      setIsOtherFrequency(false);
      setFrequency(value);
    }
  };

  const handleSubmit = () => {
    if (!medicationName.trim() || !dosage.trim() || !frequency.trim()) {
      return;
    }

    const data = {
      name: medicationName.trim(),
      dosage: dosage.trim(),
      frequency: frequency.trim(),
      administrationNotes: administrationNotes.trim() || null,
    };

    if (isEditing) {
      updateMutation.mutate({ medicationId: medication.id, body: data });
    } else {
      createMutation.mutate(data);
    }
  };

  const canSubmit = medicationName.trim() && dosage.trim() && frequency.trim() && !isLoading;

  return (
    <Modal isOpen={isOpen} size="md" onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEditing ? "Edit Medication" : "Add New Medication"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Medication</FormLabel>
              <Popover
                matchWidth
                isOpen={isPopoverOpen}
                placement="bottom-start"
                onClose={onPopoverClose}
              >
                <PopoverTrigger>
                  <Input
                    ref={inputRef}
                    isDisabled={isEditing}
                    placeholder="Search for medication name"
                    value={medicationName}
                    onChange={(e) => handleMedicationNameChange(e.target.value)}
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverBody p={0}>
                    {isSearching ? (
                      <Box p={4} textAlign="center">
                        <Spinner size="sm" />
                      </Box>
                    ) : predictions.length > 0 ? (
                      <List maxH="200px" overflowY="auto">
                        {predictions.map((prediction, i) => (
                          <ListItem
                            key={i}
                            _hover={{ bg: "gray.50" }}
                            cursor="pointer"
                            p={3}
                            onClick={() => handlePredictionSelect(prediction)}
                          >
                            {prediction.name}
                          </ListItem>
                        ))}
                      </List>
                    ) : searchTerm.length > 0 ? (
                      <Box color="gray.500" p={4} textAlign="center">
                        No medications found
                      </Box>
                    ) : null}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Dose</FormLabel>
              {selectedPrediction?.doses !== undefined && selectedPrediction.doses.length > 0 ? (
                <VStack align="stretch" spacing={2}>
                  <Select
                    placeholder="Select dose"
                    value={isOtherDose ? "other" : dosage}
                    onChange={(e) => handleDoseChange(e.target.value)}
                  >
                    {selectedPrediction.doses.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </Select>
                  {isOtherDose && (
                    <Input
                      placeholder="Enter custom dose..."
                      value={dosage}
                      onChange={(e) => setDosage(e.target.value)}
                    />
                  )}
                </VStack>
              ) : (
                <Input
                  placeholder="Enter dose"
                  value={dosage}
                  onChange={(e) => setDosage(e.target.value)}
                />
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Frequency</FormLabel>
              <VStack align="stretch" spacing={2}>
                <Select
                  placeholder="Select frequency"
                  value={isOtherFrequency ? "other" : frequency}
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                >
                  {MEDICATION_FREQUENCY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                  <option value="other">Other</option>
                </Select>
                {isOtherFrequency && (
                  <Input
                    placeholder="Enter custom frequency..."
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                  />
                )}
              </VStack>
            </FormControl>

            <FormControl>
              <FormLabel>Administration Notes</FormLabel>
              <Textarea
                placeholder="Enter administration notes (optional)"
                rows={3}
                value={administrationNotes}
                onChange={(e) => setAdministrationNotes(e.target.value)}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button isDisabled={isLoading} mr={3} variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!canSubmit}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {isEditing ? "Update" : "Save"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
